import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import VerificationsForm from './VerificationsForm';
import VerificationsList from './VerificationsList';
import VerificationsHistory from './VerificationsHistory';
import VerificationsQuickActionModal from './VerificationsQuickActionModal';
import { loadVerifications } from '../api';

const VerificationsSection = ({ currentUser }) => {
  const { showNotification } = useNotifications();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionRecord, setQuickActionRecord] = useState(null);

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      const userEmail = currentUser ? currentUser.email : null;
      console.log('🔍 VerificationsSection - Cargando con células del usuario:', userCells);
      console.log('🔍 VerificationsSection - Rol del usuario:', userRole);
      console.log('🔍 VerificationsSection - Email del usuario:', userEmail);
      
      const recordsData = await loadVerifications(userCells, userRole, userEmail);
      setRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading verifications:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (recordData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const url = editingRecord 
        ? `${API_URL}/verifications/${editingRecord.id}`
        : `${API_URL}/verifications`;
      
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...recordData,
          user_name: currentUser?.name || currentUser?.email || 'Sistema'
        }),
      });

      if (response.ok) {
        showNotification(editingRecord 
          ? 'Registro actualizado exitosamente' 
          : 'Registro creado exitosamente', 'success');
        setShowForm(false);
        setEditingRecord(null);
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      showNotification(`Error al guardar: ${error.message}`, 'error');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      return;
    }

    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/verifications/${recordId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showNotification('Registro eliminado exitosamente', 'success');
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar el registro');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      showNotification(`Error al eliminar: ${error.message}`, 'error');
    }
  };

  const handleShowHistory = (record) => {
    setSelectedRecord(record);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setQuickActionRecord(record);
    setShowQuickAction(true);
  };

  const handleQuickActionSave = async (recordId, actionData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      // Si es un cambio de estado, obtener el registro completo y actualizarlo
      if (actionData.action_type === 'status_changed') {
        // Obtener el registro actual
        const currentRecord = records.find(r => r.id === recordId);
        if (!currentRecord) {
          throw new Error('Registro no encontrado');
        }

        // Crear objeto con todos los campos necesarios
        const updateData = {
          mes: currentRecord.mes,
          seguimiento: currentRecord.seguimiento,
          fecha_asunto: currentRecord.fecha_asunto ? new Date(currentRecord.fecha_asunto).toISOString().split('T')[0] : null,
          nombre_cliente: currentRecord.nombre_cliente,
          cuenta_publicitaria: currentRecord.cuenta_publicitaria,
          celula: currentRecord.celula,
          campana: currentRecord.campana,
          asunto: currentRecord.asunto,
          acciones: currentRecord.acciones,
          status: actionData.new_status, // Solo cambiar el status
          updated_by: actionData.user_name
        };

        const updateResponse = await fetch(`${API_URL}/verifications/${recordId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(errorData.error || 'Error al actualizar el estado');
        }
      }

      // Luego registrar la acción en el historial
      const response = await fetch(`${API_URL}/verifications/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (response.ok) {
        showNotification('Acción registrada exitosamente', 'success');
        setShowQuickAction(false);
        setQuickActionRecord(null);
        
        // Recargar la lista para reflejar los cambios
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al registrar la acción');
      }
    } catch (error) {
      console.error('Error al registrar acción:', error);
      throw error; // Re-lanzar para que el modal pueda manejarlo
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando registros...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">🔍 Verificaciones</h1>
            <p className="text-purple-100 mt-2">
              Gestión de verificaciones y validaciones de campañas publicitarias
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{records.length}</div>
            <div className="text-purple-100">Registros Activos</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setEditingRecord(null);
              setShowForm(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <span className="mr-2">➕</span>
            Nueva Verificación
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            <span className="text-sm text-gray-600">En Proceso</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Completado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Cancelado</span>
          </div>
        </div>
      </div>

      {/* Lista de registros */}
      <VerificationsList
        records={records}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onShowHistory={handleShowHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
      />

      {/* Modales */}
      {showForm && (
        <VerificationsForm
          record={editingRecord}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {showHistory && selectedRecord && (
        <VerificationsHistory
          record={selectedRecord}
          onClose={() => {
            setShowHistory(false);
            setSelectedRecord(null);
          }}
        />
      )}

      {showQuickAction && quickActionRecord && (
        <VerificationsQuickActionModal
          record={quickActionRecord}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionRecord(null);
          }}
          onSave={handleQuickActionSave}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default VerificationsSection;
